/**
 * HustleUpPublicEventPage — /up/e/:event_id
 *
 * Public single-event landing page.  Injects JSON-LD Event schema for
 * Google rich-card eligibility, OG tags for share cards, and an "Add to
 * calendar" button that downloads the ICS file already exposed by
 * /api/hustleup/events/{id}/calendar.ics.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Calendar, Globe, Loader2,
         Users, CalendarPlus, Share2, ArrowRight } from "lucide-react";
import "./HustleUpPublicGroupPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

function fmt(iso) {
  if (!iso) return "TBA";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", {
      weekday: "long", day: "numeric", month: "long", year: "numeric"
    }) + " · " + d.toLocaleTimeString("en-AU", {
      hour: "numeric", minute: "2-digit"
    });
  } catch (_) { return iso; }
}

function setMeta(name, content, property = false) {
  if (!content) return;
  const attr = property ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function injectJsonLd(id, payload) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

export default function HustleUpPublicEventPage() {
  const { event_id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let dead = false;
    setLoading(true);
    api.get(`/api/hustleup/public/events/${event_id}`)
      .then((r) => {
        if (dead) return;
        const e = r.data;
        setEv(e);
        document.title = `${e.title} · ${e.group?.name} · HustleUp`;
        const desc = (e.description || "").slice(0, 160);
        setMeta("description", desc);
        setMeta("og:title", e.title, true);
        setMeta("og:description", desc, true);
        setMeta("og:type", "event", true);
        setMeta("og:url", e.public_url, true);
        if (e.image_url) setMeta("og:image", e.image_url, true);
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", e.title);
        setMeta("twitter:description", desc);
        // JSON-LD event schema
        injectJsonLd("hu-jsonld-event", {
          "@context": "https://schema.org",
          "@type": "Event",
          "name": e.title,
          "description": desc,
          "startDate": e.starts_at,
          "endDate": e.ends_at || e.starts_at,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": e.is_online
            ? "https://schema.org/OnlineEventAttendanceMode"
            : "https://schema.org/OfflineEventAttendanceMode",
          "location": e.is_online
            ? { "@type": "VirtualLocation", "url": e.public_url }
            : { "@type": "Place",
                "name": e.venue_name || e.group?.region,
                "address": e.venue_addr || e.group?.region },
          "image": e.image_url || e.group?.image_url,
          "organizer": { "@type": "Organization", "name": e.group?.name },
          "offers": {
            "@type": "Offer",
            "price": "0", "priceCurrency": "AUD",
            "availability": "https://schema.org/InStock",
            "url": e.public_url,
          },
        });
      })
      .catch((err) => {
        if (!dead) setError(err?.response?.status === 404 ? "Event not found." : "Could not load.");
      })
      .finally(() => { if (!dead) setLoading(false); });
    return () => {
      dead = true;
      const el = document.getElementById("hu-jsonld-event");
      if (el) el.remove();
    };
  }, [event_id]);

  const onRsvp = () => {
    const token = localStorage.getItem("hu_session_token");
    if (token) {
      navigate(`/hustleup/groups/${ev.group.slug}`);
    } else {
      navigate(`/hustleup/join?return=${encodeURIComponent(`/up/e/${event_id}`)}`);
    }
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: ev.title,
          text: (ev.description || "").slice(0, 80), url: ev.public_url });
      } else {
        await navigator.clipboard.writeText(ev.public_url);
        alert("Link copied to clipboard");
      }
    } catch (_) { /* user cancelled or no clipboard */ }
  };

  if (loading) return (
    <div className="hupg-root">
      <div className="hupg-loading"><Loader2 className="hupg-spin"/> Loading…</div>
    </div>
  );
  if (error || !ev) return (
    <div className="hupg-root">
      <header className="hupg-topbar">
        <Link to="/up/groups" className="hupg-back"><ArrowLeft size={14}/> All groups</Link>
      </header>
      <div className="hupg-error">{error || "Event not found."}</div>
    </div>
  );

  return (
    <div className="hupg-root" data-testid="hu-public-event-root">
      <header className="hupg-topbar">
        <Link to={`/up/g/${ev.group.slug}`} className="hupg-back">
          <ArrowLeft size={14}/> {ev.group.name}
        </Link>
        <div className="hupg-topbar__right">
          <button onClick={share} className="hupg-share">
            <Share2 size={14}/> Share
          </button>
          <button onClick={onRsvp} className="hupg-cta" data-testid="hu-public-event-rsvp">
            RSVP <ArrowRight size={14}/>
          </button>
        </div>
      </header>

      <section
        className="hupg-hero"
        style={ev.image_url ? { backgroundImage:
          `linear-gradient(180deg, rgba(15,23,42,0.10), rgba(15,23,42,0.80)), url(${ev.image_url})` }
          : undefined}
      >
        <div className="hupg-hero__inner">
          <span className="hupg-hero__cat">
            <Calendar size={11}/> Event
          </span>
          <h1 className="hupg-hero__title">{ev.title}</h1>
          <div className="hupg-hero__meta">
            <span><Calendar size={12}/> {fmt(ev.starts_at)}</span>
            <span>
              {ev.is_online
                ? <><Globe size={12}/> Online</>
                : <><MapPin size={12}/> {ev.venue_name || ev.venue_addr}</>}
            </span>
            <span><Users size={12}/> {ev.rsvp_yes} going{ev.capacity ? ` / ${ev.capacity}` : ""}</span>
          </div>
        </div>
      </section>

      <main className="hupg-body">
        <section className="hupg-section">
          <h2 className="hupg-section__h">About this event</h2>
          <p className="hupg-about" data-testid="hu-public-event-desc">
            {ev.description}
          </p>
        </section>

        <section className="hupg-section">
          <h2 className="hupg-section__h">Hosted by</h2>
          <Link to={`/up/g/${ev.group.slug}`} className="hupd-card"
                style={{ maxWidth: 360, textDecoration: "none" }}>
            <div className="hupd-card__cat">{ev.group.region}</div>
            <h3 className="hupd-card__name">{ev.group.name}</h3>
            <div className="hupd-card__meta">
              <span><Users size={11}/> {ev.group.member_count} members</span>
            </div>
            <span className="hupd-card__cta">
              View group <ArrowRight size={13}/>
            </span>
          </Link>
        </section>

        <section className="hupg-section">
          <h2 className="hupg-section__h">Add to calendar</h2>
          <a href={ev.ics_url}
             className="hupg-tail__cta"
             style={{ background: "#0f172a", color: "#fbbf24",
                       textDecoration: "none", padding: "12px 22px" }}
             data-testid="hu-public-event-ics">
            <CalendarPlus size={14}/> Download .ics file
          </a>
        </section>

        <section className="hupg-tail">
          <h3>Save your spot</h3>
          <p>Free. No subscription. Hosted by locals.</p>
          <button onClick={onRsvp} className="hupg-tail__cta">
            RSVP now <ArrowRight size={14}/>
          </button>
        </section>
      </main>

      <footer className="hupg-footer">
        Powered by Hustle<span style={{ color: "#fbbf24" }}>Up</span>.au
        · Free to host. Free to attend.
      </footer>
    </div>
  );
}
